/**
 * Slugged job-detail URLs: `/jobs/{slug}-{id}`.
 *
 * The trailing numeric `id` is the stable, canonical key — the human-readable
 * slug is decorative and may change without breaking the URL (a stale slug
 * 308-redirects to the current one; see src/app/jobs/[id]/page.tsx). Keeping
 * the id means a re-titled job never orphans its inbound links.
 *
 * Pure and dependency-free (safe on server + edge). Accent-folding matters:
 * many vacancies are Spanish or French ("Análisis…", "Suivi‑Évaluation…"), and
 * we want clean ASCII slugs ("analisis…", "suivi-evaluation…") rather than
 * percent-encoded UTF-8 in the URL bar.
 */

/** Fields the slug is built from — a structural subset of JobListItem/JobDetail. */
export interface JobSlugInput {
  id: number;
  title: string;
  orgAcronym?: string | null;
  orgName?: string | null;
  locations?: { city?: string; country?: string }[] | null;
}

/** Longest slug body (before the "-{id}" suffix). Keeps URLs readable + tidy. */
const MAX_SLUG_LEN = 80;

/**
 * A handful of Latin letters that Unicode NFD does NOT decompose into
 * base + combining mark (so stripping marks alone leaves them intact). We map
 * these explicitly before folding. Covers the ones that actually show up in our
 * feeds (German ß, Nordic/OE ligatures, Polish ł, Icelandic þ/ð, etc.).
 */
const CHAR_MAP: Record<string, string> = {
  ß: "ss",
  æ: "ae",
  Æ: "ae",
  œ: "oe",
  Œ: "oe",
  ø: "o",
  Ø: "o",
  ł: "l",
  Ł: "l",
  đ: "d",
  Đ: "d",
  ð: "d",
  Ð: "d",
  þ: "th",
  Þ: "th",
  ı: "i",
  "’": "",
  "'": "",
};

/**
 * Transliterate to lowercase ASCII: map the non-decomposable letters, then use
 * Unicode NFD to split accented letters into base + combining marks and drop
 * the marks (é→e, ñ→n, ü→u, à→a, ç→c, …).
 */
function foldToAscii(input: string): string {
  const mapped = input.replace(
    /[ßæÆœŒøØłŁđĐðÐþÞı’']/g,
    (ch) => CHAR_MAP[ch] ?? ch,
  );
  return mapped
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip diacritical marks
    .toLowerCase();
}

/**
 * Slugify arbitrary text: accent-fold → lowercase → non-alphanumeric to "-" →
 * collapse repeats → trim → cap length (never leaving a trailing dash).
 */
export function slugify(input: string): string {
  const slug = foldToAscii(input)
    .replace(/[^a-z0-9]+/g, "-") // non-alnum runs → single dash
    .replace(/^-+|-+$/g, ""); // trim leading/trailing dashes
  if (slug.length <= MAX_SLUG_LEN) return slug;
  // Cap without cutting mid-word where possible, and never end on a dash.
  return slug.slice(0, MAX_SLUG_LEN).replace(/-+[^-]*$/, "").replace(/-+$/, "");
}

/** First usable city across a job's locations ("" when none/only non-geo). */
function primaryCity(job: JobSlugInput): string {
  for (const loc of job.locations ?? []) {
    const city = loc.city?.trim();
    if (city) return city;
  }
  return "";
}

/**
 * The slug body + stable id suffix: `{title org-acronym city}-{id}`, e.g.
 * "finance-officer-imf-washington-dc-12". Falls back to bare "{id}" if the
 * descriptive part folds away to nothing (empty/emoji-only titles).
 */
export function jobSlug(job: JobSlugInput): string {
  const parts = [job.title, job.orgAcronym || job.orgName, primaryCity(job)]
    .filter((p): p is string => Boolean(p && p.trim()));
  const body = slugify(parts.join(" "));
  return body ? `${body}-${job.id}` : String(job.id);
}

/** Canonical site-relative path for a job detail page. */
export function jobPath(job: JobSlugInput): string {
  return `/jobs/${jobSlug(job)}`;
}

/**
 * Extract the numeric job id from a `[id]` route segment that may be either the
 * slugged form (`finance-officer-imf-12`) or a bare id (`12`). The id is always
 * the numeric tail after the last "-", so we take that; a bare all-digits
 * segment is the id itself. Returns null when there is no valid trailing id.
 *
 * Guards against overflow (ids beyond Number.MAX_SAFE_INTEGER) and non-integer
 * junk so callers get a clean number | null.
 */
export function parseJobId(param: string): number | null {
  const raw = param.trim();
  if (!raw) return null;
  // Bare numeric segment → the id itself.
  const tail = /^\d+$/.test(raw) ? raw : raw.slice(raw.lastIndexOf("-") + 1);
  if (!/^\d{1,15}$/.test(tail)) return null;
  const id = Number.parseInt(tail, 10);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}
